// ET character definitions (characters/**/*.char, BG_ParseCharacterFile) and
// the character every player is drawn with.

import { tokenize } from '../../shared/tokenize';

export interface CharacterDef {
  /** Skin name: the body skin is <mesh without extension>_<skin>.skin, accessories likewise. */
  skin: string;
  mesh: string;
  animationGroup: string;
  animationScript: string;
  hudHead: string;
  hudHeadAnims: string;
  hudHeadSkin: string;
}

/** One model for all players: the Axis soldier, holding a knife. */
export const PLAYER_CHARACTER = 'characters/temperate/axis/soldier.char';
/** Third person knife (weapon_knife's world model; ET loads the .mdc when there is no .md3). */
export const PLAYER_WEAPON = 'models/multiplayer/knife/knife.md3';
export const PLAYER_WEAPON_SKIN = 'models/multiplayer/knife/knife_axis.skin';
/** The weapon as the animation script calls it (its pickup name). */
export const PLAYER_WEAPON_NAME = 'knife';
/**
 * The knife in first person (weapons/knife.weap): the hands model (only tags,
 * animated by weapon.cfg; idle is frame 0), the arm on its tag_weapon with
 * the team's skin (Axis, Allies) and the blade (weaponLink part 0) on tag_barrel.
 */
export const VIEW_WEAPON = {
  hands: 'models/multiplayer/knife/v_knife_hand.md3',
  model: 'models/multiplayer/knife/v_knife.md3',
  skins: ['models/multiplayer/knife/knife_axis.skin', 'models/multiplayer/knife/knife_allied.skin'],
  parts: [{ tag: 'tag_barrel', model: 'models/multiplayer/knife/v_knife_barrel.md3' }],
} as const;

export function parseCharacter(text: string): CharacterDef {
  const tokens = tokenize(text).map((t) => t.text);
  const def: CharacterDef = { skin: '', mesh: '', animationGroup: '', animationScript: '', hudHead: '', hudHeadAnims: '', hudHeadSkin: '' };
  const keys: Record<string, keyof CharacterDef> = {
    skin: 'skin',
    mesh: 'mesh',
    animationgroup: 'animationGroup',
    animationscript: 'animationScript',
    hudhead: 'hudHead',
    hudheadanims: 'hudHeadAnims',
    hudheadskin: 'hudHeadSkin',
  };
  if (tokens[0]?.toLowerCase() !== 'characterdef' || tokens[1] !== '{') throw new Error("character: expected 'characterDef {'");
  for (let i = 2; i < tokens.length && tokens[i] !== '}'; i += 2) {
    const key = keys[tokens[i].toLowerCase()];
    if (key) def[key] = tokens[i + 1] ?? '';
  }
  if (!def.mesh || !def.animationGroup || !def.animationScript) throw new Error('character: mesh, animationgroup and animationscript are required');
  return def;
}

export const stripExtension = (path: string): string => path.replace(/\.[^./\\]*$/, '');

/** Shader name as R_FindShader looks it up: forward slashes, no extension, lower case. */
export const shaderKey = (name: string): string => stripExtension(name.replace(/\\/g, '/')).toLowerCase();

/** Body skin file of a character. */
export const bodySkinPath = (def: CharacterDef): string => `${stripExtension(def.mesh)}_${def.skin}.skin`;

/** Skin of an accessory model (CG_RegisterAcc). */
export const accessorySkinPath = (model: string, skin: string): string => `${stripExtension(model)}_${skin}.skin`;

/** The file a model name loads: as named, or with the other vertex model extension (.md3 <-> .mdc). */
export function modelFile(has: (path: string) => boolean, name: string): string | null {
  const n = name.replace(/\\/g, '/').toLowerCase();
  if (has(n)) return n;
  const alt = n.endsWith('.md3') ? `${n.slice(0, -4)}.mdc` : n.endsWith('.mdc') ? `${n.slice(0, -4)}.md3` : null;
  return alt && has(alt) ? alt : null;
}

/** Accessory types drawn on the body, and on the head (cg_character.c). Rank is left out: players have no rank. */
export const BODY_ACCESSORIES: Record<string, string> = {
  md3_beltr: 'tag_bright',
  md3_beltl: 'tag_bleft',
  md3_belt: 'tag_ubelt',
  md3_back: 'tag_back',
  md3_weapon: 'tag_weapon',
  md3_weapon2: 'tag_weapon2',
};
export const HEAD_ACCESSORIES: Record<string, string> = {
  md3_hat: 'tag_mouth',
  md3_hat2: 'tag_mouth',
  md3_hat3: 'tag_mouth',
};
